import { CachePolicies, useFetch } from 'use-http';
import { MetricKind, MetricValue } from '@/webapi/models';
import { routes } from '@/webapi/routes';

export function useAnalyticsApi() {
  const { post, get, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      cacheLife: 15 * 60,
      loading: true,
    },
  }));

  const getCustomMetrics = () =>
    get(routes.customMetrics) as Promise<{
      metrics: Array<{ name: string; id: number }>;
    }>;

  const getAnalyticsRequestBody = (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version = 0,
    expEndDate: string | undefined = undefined,
    sessionType = ``,
    audience = ``,
    activeVariants: string[] = undefined,
    isHidden = false,
    outlierSmooth = false,
    isPostPurchase = false,
    customMetrics: Array<{ name: string; ordinal: number }> = [],
  ) => {
    const v = isHidden ? -1 : version;
    const body = {
      experienceId,
      goal,
      startAt: format(startAt),
      endAt: format(endAt),
      version: v,
      sessionType,
      activeVariants,
      ...(expEndDate ? { expEndDate: format(new Date(expEndDate)) } : {}),
      outlierSmooth,
      isPostPurchase,
      customMetrics,
    } as any;
    if (audience) {
      body.audiences = [audience];
    }
    return body;
  };

  const getDailySummary = async (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version = 0,
    expEndDate: string | undefined = undefined,
    sessionType = ``,
    audience = ``,
    activeVariants: string[] = undefined,
    isHidden = false,
    outlierSmooth = false,
    isPostPurchase = false,
    customMetrics: Array<{ name: string; ordinal: number }> = [],
  ): Promise<DailySummaryResponse> =>
    post(
      routes.dailySummaryResults(),
      getAnalyticsRequestBody(
        experienceId,
        goal,
        startAt,
        endAt,
        version,
        expEndDate,
        sessionType,
        audience,
        activeVariants,
        isHidden,
        outlierSmooth,
        isPostPurchase,
        customMetrics,
      ),
    );

  const getSummary = async (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version = 0,
    expEndDate: string | undefined = undefined,
    sessionType = ``,
    audience = ``,
    activeVariants: string[] = [],
    sections: string[] = [],
    outlierSmooth = false,
    isPostPurchase = false,
    isOtwForAll = false,
    customMetrics: Array<{ name: string; ordinal: number }> = [],
    baselineVariantId = ``,
    variantChances: Record<string, number> = {},
  ): Promise<SummaryResponse> => {
    const body = {
      experienceId,
      goal,
      startAt: format(startAt),
      endAt: format(endAt),
      sections,
      version,
      sessionType,
      activeVariants,
      ...(expEndDate ? { expEndDate: format(new Date(expEndDate)) } : {}),
      outlierSmooth,
      isPostPurchase,
      isOtwForAll,
      customMetrics,
      ...(baselineVariantId ? { baselineVariantId } : {}),
      variantChances,
    } as any;
    if (audience) {
      body.audiences = [audience];
    }
    return post(routes.summaryResults(), body);
  };

  const getVersions = async (
    experienceId: string,
  ): Promise<ExpVersionsResponse> =>
    post(routes.experienceVersions(), {
      value: experienceId,
    });

  return {
    loading,
    getSummary,
    getVersions,
    getDailySummary,
    getCustomMetrics,
  };
}

export interface ExpVersionsResponse {
  versions: Array<ExpVersion>;
  maxVersion: ExpVersion;
}

export interface ExpVersion {
  version: number;
  name?: string;
  isCurrent?: boolean;
  expEndDate?: Date;
  startDate: string;
  variantChances: Record<string, number>;
  endDate: string;
}

export interface DailySummaryResponse {
  experienceId: string;
  variant: Record<string, DailySummary>;
}

export interface DailySummary {
  metrics: DailyMetrics[];
}

export interface DailyMetrics {
  day: string;
  metrics: MetricValue[];
}

export interface SummaryResponse {
  experienceId: string;
  controlVariationId: string;
  variants: VariantSummary[];
  hasDataForAllAudiences?: boolean;

  assistedProducts: Array<AssistedProduct>;
  topProducts?: Array<TopProduct>;
  bestSellers?: Array<TopProduct>;
  returns?: Array<ReturnedProduct>;

  revenueGauss?: AbTestResult;
  conversionsBinomal?: AbTestResult;
  version?: number;
  statisticalSignificance?: StatisticalSignificanceResponse;
}

export interface StatisticalSignificanceResponse {
  experienceId: string;
  version: number;
  status: string;
  isSignificant: boolean;
  controlVariantId?: string;
  winningVariantId?: string;
  chanceToWinPercent: number;
  upliftPercent: number;
  actualSampleSize: number;
  requiredSampleSize: number;
  evaluatedGoal: MetricKind;
  error?: string;
}

export interface ReturnedProduct {
  productId: string;
  variantId: string;
  title: string;
  imageSrc: string;
  returns: number;
  items: number;
}

export interface TopProduct {
  hidden?: boolean;
  productId: string;
  variantId: string;
  title: string;
  imageSrc: string;
  profit: number;
  revenue: number;
  items: number;
}

export interface AssistedProduct {
  type: string;
  productId: string;
  variantId: string;
  title: string;
  imageSrc: string;
  revenue: string;
  items: string;
}

export interface VariantSummary {
  variantId: string;

  metrics: MetricValue[];
  revenueGauss?: AbTestResult;
  conversionsBinomal?: AbTestResult;
}

export interface AbTestResult {
  expected: number;
  chanceToWin: number;
  ci: number[];
  risk: number[];
  minSampleSize?: string;
  uplift: {
    mean: number;
    std: number;
    dist: string;
  };
}

export function format(date: Date) {
  const utcDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  let utcMonth: any = utcDate.getUTCMonth() + 1;
  if (utcMonth < 10) {
    utcMonth = `0${utcMonth}`;
  }
  let utcDay: any = utcDate.getUTCDate();
  if (utcDay < 10) {
    utcDay = `0${utcDay}`;
  }
  return `${utcDate.getUTCFullYear()}-${utcMonth}-${utcDay}T00:00:00.000Z`;
}
